'use strict'
import React, { useState, useEffect, useCallback } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { isBefore, isAfter, isEqual } from 'date-fns'
import ja from 'date-fns/locale/ja'
import { SpinnerButton } from '../../../common/CustomButton.js'
import { getMessage } from '../../../../common/utils/MessageUtils.js'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('ja', ja)

// 禁則文字リスト
const disallowedChars = [',', '\\', '"']

/**
 * 指定された日付の月の初日を取得
 *
 * @param {Date} date - 対象の日付
 * @returns {Date} 月の初日（その月の1日）
 */
const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1)

/**
 * 指定された日付の月の末日を取得
 *
 * @param {Date} date - 対象の日付
 * @returns {Date} 月の末日
 */
const getLastDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0)

/**
 * 日付を YYYY-MM-DD 形式に変換（タイムゾーンのズレを回避）
 *
 * @param {Date | null} date
 * @returns {string}
 */
function formatDateToYMD(date) {
  if (!date) return ''
  const year = date.getFullYear()
  const month = ('0' + (date.getMonth() + 1)).slice(-2)
  const day = ('0' + date.getDate()).slice(-2)
  return `${year}-${month}-${day}`
}

/*==================================
    ユーティリティ関数
  ===================================*/

/**
 * エラーメッセージを表示するコンポーネント
 *
 * @param {Object} props - コンポーネントのプロパティ
 * @param {Object<string, string[]>} props.errors - エラーメッセージのオブジェクト（フィールド名をキー、エラーメッセージの配列を値とする）
 * @param {string} [props.field=''] - 表示するエラーメッセージのフィールド名. Default is `''`
 * @returns {JSX.Element} エラーメッセージを表示する要素（エラーがない場合は空の要素）
 */
export const ShowErrors = React.memo(({ errors: errorMessage, field = '' }) => {
  if (!errorMessage?.[field]) return <></>

  return (
    <>
      {errorMessage[field].map((err, index) => (
        <p key={index} className="red--text">
          {err}
        </p>
      ))}
    </>
  )
})

/**
 * 2つの期間が重複しているか判定
 *
 * @param {string} registeredStartDate - 登録済みデータの有効開始日（YYYY-MM-DD または空文字）
 * @param {string} registeredEndDate - 登録済みデータの有効終了日（YYYY-MM-DD または空文字）
 * @param {string} inputStartDate - 入力データの有効開始日（YYYY-MM-DD または空文字）
 * @param {string} inputEndDate - 入力データの有効終了日（YYYY-MM-DD または空文字）
 * @returns {boolean} 期間が重複していれば true
 */
export function isPeriodOverlap(registeredStartDate, registeredEndDate, inputStartDate, inputEndDate) {
  const toDate = (dateStr, isStart) => {
    if (!dateStr) {
      // 開始日が空文字なら過去の十分古い日付に設定（無期限とみなす）
      // 終了日が空文字なら未来の十分未来の日付に設定（無期限とみなす）
      return isStart ? new Date('1900-01-01') : new Date('9999-12-31')
    }
    return new Date(dateStr)
  }

  const regStart = toDate(registeredStartDate, true)
  const regEnd = toDate(registeredEndDate, false)
  const inputStart = toDate(inputStartDate, true)
  const inputEnd = toDate(inputEndDate, false)

  // 期間の重複条件：開始日 <= 相手の終了日 かつ 終了日 >= 相手の開始日
  const startCondition = isBefore(regStart, inputEnd) || isEqual(regStart, inputEnd)
  const endCondition = isAfter(regEnd, inputStart) || isEqual(regEnd, inputStart)
  return startCondition && endCondition
}

/**
 * フォーム入力フィールドを表示するコンポーネント
 *
 * @param {Object} props - コンポーネントのプロパティ
 * @param {string} props.id - 入力フィールドのID
 * @param {string} props.label - ラベルテキスト
 * @param {boolean} [props.required=true] - 必須項目かどうか. Default is `true`
 * @param {string} [props.placeholder] - プレースホルダーテキスト
 * @param {string} props.value - 入力値
 * @param {Object<string, string[]>} [props.errors] - エラーメッセージのオブジェクト
 * @param {boolean} [props.disabled] - 無効化フラグ
 * @param {function(React.ChangeEvent<HTMLInputElement>): void} [props.onChange] - 変更時のハンドラ
 * @param {function(React.FocusEvent<HTMLInputElement>): void} [props.onBlur] - フォーカスアウト時のハンドラ
 * @returns {JSX.Element} フォーム入力フィールドのテーブル行
 */
const FormInput = React.memo(
  ({ id, label, required = true, placeholder, value, errors, disabled, onChange, onBlur }) => {
    const hasError = errors && errors[id] && errors[id].length > 0

    return (
      <tr>
        <th className="form-table__col-head w-[150px]">
          <label htmlFor={id} className="text-h7 font-bold">
            {label} {required && <span className="red--text">*</span>}
          </label>
        </th>
        <td className="form-control form-table__col-data">
          <input
            id={id}
            name={id}
            className={`text-input w-full text-left ${hasError ? 'is-error' : ''}`}
            type="text"
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
          />
          <ShowErrors field={id} errors={errors} />
        </td>
      </tr>
    )
  }
)

/**
 * セグメント情報の登録・編集モーダルコンテンツコンポーネント
 *
 * @param {Object} props - コンポーネントのプロパティ
 * @param {Object[]} props.segmentInfo - 登録済みのセグメント情報の配列
 * @param {boolean} props.editMode - 編集モードかどうか
 * @param {Object} [props.updateData] - 編集時の更新データ
 * @param {string} [props.updateData.searchKey] - 検索用キー
 * @param {string} [props.updateData.segmentCode] - セグメントコード（内部保持・画面非表示）
 * @param {string} [props.updateData.segmentName] - セグメント名
 * @param {string} [props.updateData.validStartDate] - 有効開始日（YYYY-MM-DD形式）
 * @param {string} [props.updateData.validEndDate] - 有効終了日（YYYY-MM-DD形式）
 * @param {function(Object, function): void} [props.onSubmit] - 送信時のコールバック関数
 * @param {function(): void} [props.onClose] - モーダルを閉じる際のコールバック関数
 * @returns {JSX.Element} セグメント情報の登録・編集フォーム
 */
/**
 * モーダル初期状態を構築する（新規は search_key と segment_code に同一 UUID を払い出す）
 *
 * @param {Object | null | undefined} updateData - 編集時の既存行。未指定時は新規登録。
 * @returns {Object} フォーム初期値
 */
function buildInitData(updateData) {
  if (updateData) {
    return {
      searchKey: updateData.searchKey,
      segmentCode: updateData.segmentCode,
      segmentName: updateData.segmentName ?? '',
      validStartDate: updateData.validStartDate ? getFirstDayOfMonth(new Date(updateData.validStartDate)) : null,
      validEndDate: updateData.validEndDate ? getLastDayOfMonth(new Date(updateData.validEndDate)) : null
    }
  }
  const id = crypto.randomUUID()
  return {
    searchKey: id,
    segmentCode: id,
    segmentName: '',
    validStartDate: null,
    validEndDate: null
  }
}

const SegmentModalContent = (props) => {
  const { segmentInfo, editMode, updateData, onSubmit, onClose } = props

  const [formData, setFormData] = useState(() => buildInitData(updateData))
  const [errors, setErrorState] = useState({})

  useEffect(() => {
    setFormData(buildInitData(updateData))
  }, [updateData])

  useEffect(() => {
    const modalWindowElement = document.querySelector('.modal-window')
    const modalWindowClassList = ['w-[initial]', 'max-h-[97vh]', 'max-w-[750px]', 'overflow-y-auto']

    modalWindowElement?.classList.add(...modalWindowClassList)
    return () => {
      modalWindowElement?.classList.remove(...modalWindowClassList)
    }
  }, [])

  /**
   * 入力フィールドの変更を処理
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - 変更イベント
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }, [])

  /**
   * 入力フィールドのフォーカスアウト時に値をトリム
   *
   * @param {React.FocusEvent<HTMLInputElement>} e - フォーカスアウトイベント
   */
  const handleBlur = useCallback((e) => {
    const { name, value } = e.target
    const trimmedValue = value.trim()
    if (value !== trimmedValue) {
      setFormData((prev) => ({ ...prev, [name]: trimmedValue }))
    }
  }, [])

  /**
   * 日付変更時（月初・月末に変換してセット）
   *
   * @param {string} fieldName - フィールド名（'validStartDate' または 'validEndDate'）
   * @param {Date | null} date - 選択された日付
   */
  const handleDateChange = useCallback((fieldName, date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, [fieldName]: null }))
      return
    }
    if (fieldName === 'validStartDate') {
      setFormData((prev) => ({ ...prev, validStartDate: getFirstDayOfMonth(date) }))
    } else if (fieldName === 'validEndDate') {
      setFormData((prev) => ({ ...prev, validEndDate: getLastDayOfMonth(date) }))
    }
  }, [])

  /**
   * 入力文字列に禁則文字が含まれているかチェック
   *
   * @param {string} [input=''] - チェック対象の文字列. Default is `''`
   * @param {string[]} [chars=disallowedChars] - チェック対象の禁則文字の配列. Default is `disallowedChars`
   * @returns {boolean} 禁則文字が含まれていれば true
   */
  const checkIfCharsExist = (input = '', chars = disallowedChars) => {
    return chars.some((char) => input.includes(char))
  }

  /**
   * フォームの入力値をバリデーション
   *
   * @returns {Object<string, string[]> | false} エラーがある場合はエラーオブジェクト、ない場合は false
   * @returns {string[]} Return.segmentCode - セグメントコードのエラーメッセージ配列
   * @returns {string[]} Return.segmentName - セグメント名のエラーメッセージ配列
   * @returns {string[]} Return.validPeriod - 有効期間のエラーメッセージ配列
   */
  const formHasErrors = () => {
    const errors = {
      segmentCode: [],
      segmentName: [],
      validPeriod: []
    }

    // 必須チェック
    // if (!formData.segmentCode?.trim()) {
    //   errors.segmentCode.push(getMessage('E010001', 'セグメントコード'))
    // }

    if (!formData.segmentName?.trim()) {
      errors.segmentName.push(getMessage('E010001', 'セグメント名'))
    }

    // 禁則文字チェック（セグメントコードはシステム払い出しのため対象外）
    if (checkIfCharsExist(formData.segmentName)) {
      errors.segmentName.push(getMessage('E010016', disallowedChars.join(' ')))
    }

    // 重複チェック関数（セグメントコード）
    const duplicatePredicate = (registeredData) => {
      // セグメントコードが空なら重複チェックしない
      if (!formData.segmentCode?.trim()) return false

      // セグメントコードが同じかつ編集中の自レコード以外
      let codeMatch = registeredData.segmentCode === formData.segmentCode
      if (editMode) {
        codeMatch = codeMatch && registeredData.searchKey !== formData.searchKey
      }
      // 期間重複判定
      const periodOverlap = isPeriodOverlap(
        registeredData.validStartDate,
        registeredData.validEndDate,
        formData.validStartDate,
        formData.validEndDate
      )
      return codeMatch && periodOverlap
    }

    // 重複あればエラーセット
    if (segmentInfo.find(duplicatePredicate)) {
      errors.segmentCode.push(getMessage('E010013', '有効期間内に同一の「セグメントコード」'))
    }

    // 重複チェック関数（セグメント名）
    const nameDuplicatePredicate = (registeredData) => {
      let nameMatch = registeredData.segmentName === formData.segmentName
      if (editMode) {
        nameMatch = nameMatch && registeredData.searchKey !== formData.searchKey
      }
      const periodOverlap = isPeriodOverlap(
        registeredData.validStartDate,
        registeredData.validEndDate,
        formData.validStartDate,
        formData.validEndDate
      )
      return nameMatch && periodOverlap
    }

    if (segmentInfo.find(nameDuplicatePredicate)) {
      errors.segmentName.push(getMessage('E010013', '有効期間内に同一の「セグメント名」'))
    }

    // 有効期間チェック
    if (formData.validStartDate && formData.validEndDate && formData.validStartDate > formData.validEndDate) {
      errors.validPeriod.push(getMessage('E011011', '有効終了月', '有効開始月'))
    }

    if (Object.values(errors).some((e) => e.length > 0)) return errors
    return false
  }

  /** フォーム送信処理 バリデーションを実行し、エラーがなければ onSubmit コールバックを呼び出す */
  const handleSubmit = () => {
    const validationErrors = formHasErrors()
    if (validationErrors) {
      setErrorState(validationErrors)
      return
    }
    setErrorState({})

    const submitData = {
      ...formData,
      segmentCode: formData.segmentCode.trim(),
      segmentName: formData.segmentName.trim(),
      validStartDate: formatDateToYMD(formData.validStartDate),
      validEndDate: formatDateToYMD(formData.validEndDate)
    }

    onSubmit?.(submitData, setFormData)
  }

  return (
    <>
      <form className="text-base">
        <table className="form-table table-fixed w-full">
          <tbody>
            {errors.segmentCode?.length > 0 && (
              <tr>
                <th className="form-table__col-head w-[150px]" />
                <td className="form-control form-table__col-data">
                  <ShowErrors field="segmentCode" errors={errors} />
                </td>
              </tr>
            )}
            <FormInput
              errors={errors}
              value={formData.segmentName}
              onChange={handleChange}
              onBlur={handleBlur}
              id="segmentName"
              label="セグメント名"
              placeholder="セグメント名を入力してください"
            />
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor={'ValidityPeriod'} className="text-h7 font-bold">
                  有効期間
                </label>
              </th>
              <td className="form-control form-table__col-data">
                <div className="flex items-center gap-2">
                  <DatePicker
                    id="validStartDate"
                    name="validStartDate"
                    selected={formData.validStartDate}
                    onChange={(date) => handleDateChange('validStartDate', date)}
                    showMonthYearPicker
                    dateFormat="yyyy/MM"
                    className={`rounded-lg w-[150px] text-input text-left ${
                      errors.validStartDate?.length > 0 || errors.validPeriod?.length > 0 ? 'is-error' : ''
                    }`}
                    placeholderText="YYYY/MM"
                    autoComplete="off"
                    locale="ja"
                    popperPlacement="bottom-start"
                    portalId="root-portal"
                    popperContainer={({ children }) => (
                      <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 1500, minWidth: 280 }}>
                        {children}
                      </div>
                    )}
                  />
                  <span className="div-mid mx-5">～</span>
                  <DatePicker
                    id="validEndDate"
                    name="validEndDate"
                    selected={formData.validEndDate}
                    onChange={(date) => handleDateChange('validEndDate', date)}
                    showMonthYearPicker
                    dateFormat="yyyy/MM"
                    className={`rounded-lg w-[150px] text-input text-left ${
                      errors.validEndDate?.length > 0 || errors.validPeriod?.length > 0 ? 'is-error' : ''
                    }`}
                    placeholderText="YYYY/MM"
                    autoComplete="off"
                    locale="ja"
                    minDate={formData.validStartDate || null}
                    popperPlacement="bottom-start"
                    portalId="root-portal"
                    popperContainer={({ children }) => (
                      <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 1500, minWidth: 280 }}>
                        {children}
                      </div>
                    )}
                  />
                </div>
                <ShowErrors field="validPeriod" errors={errors} />
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      <div className="modal-content-footer mt-3">
        <div className="footer_button_area gap-x-16">
          <button className="footer-btn button_back" onClick={onClose}>
            キャンセル
          </button>
          <SpinnerButton name="設定" className="footer-btn button_base" onClick={handleSubmit} />
        </div>
      </div>
    </>
  )
}

export default SegmentModalContent
